import { useEffect, useState } from "react";
import { Star, Check, EyeOff, Trash2, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";
import { fetchReviews, updateReview, deleteReview, apiError } from "@/lib/admin";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const BADGE = {
    pending: "bg-[#FAF0D7] text-[#8a6a10] border-[#D4AF37]/40",
    approved: "bg-[#E9F3EA] text-[#2f6b3a] border-[#bfe0c5]",
    hidden: "bg-[#F1ECE7] text-[#6B4A3E] border-[#e2d8d0]",
};

export default function AdminReviews({ onLogout, onCountChange }) {
    const [reviews, setReviews] = useState(null);
    const [filter, setFilter] = useState("pending");

    const load = async () => {
        try {
            const data = await fetchReviews();
            setReviews(data);
            onCountChange?.(data.filter((r) => r.status === "pending").length);
        } catch (e) {
            if (e?.response?.status === 401) return onLogout();
            toast.error(apiError(e));
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setStatus = async (id, status) => {
        try {
            const updated = await updateReview(id, status);
            setReviews((list) => {
                const next = list.map((r) => (r.id === id ? updated : r));
                onCountChange?.(next.filter((r) => r.status === "pending").length);
                return next;
            });
            toast.success(status === "approved" ? "Review is now live" : "Review hidden");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await deleteReview(id);
            setReviews((list) => {
                const next = list.filter((r) => r.id !== id);
                onCountChange?.(next.filter((r) => r.status === "pending").length);
                return next;
            });
            toast.success("Review deleted");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const visible = (reviews || []).filter((r) => filter === "all" || r.status === filter);
    const count = (s) => (reviews || []).filter((r) => r.status === s).length;

    return (
        <div data-testid="admin-reviews">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Customer Reviews</p>
                    <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#3D2318]">
                        {reviews ? `${count("pending")} waiting for approval` : "Loading…"}
                    </h1>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="admin-review-filters">
                    {["pending", "approved", "hidden", "all"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            data-testid={`admin-review-filter-${s}`}
                            className={`rounded-full border px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${filter === s ? "bg-[#3D2318] text-[#F8D7E3] border-[#3D2318]" : "bg-white text-[#6B4A3E] border-[#F2E1E6] hover:border-[#C95B7D]"}`}
                        >
                            {s}{s !== "all" && reviews ? ` · ${count(s)}` : ""}
                        </button>
                    ))}
                </div>
            </div>

            {reviews && visible.length === 0 && (
                <div className="mt-12 flex flex-col items-center rounded-[2rem] border border-dashed border-[#F2E1E6] bg-white/60 p-12 text-center" data-testid="admin-reviews-empty">
                    <MessageSquareHeart className="h-10 w-10 text-[#C95B7D]" />
                    <p className="mt-4 font-display text-xl font-bold text-[#3D2318]">No {filter === "all" ? "" : filter} reviews</p>
                    <p className="mt-1 text-sm text-[#6B4A3E]">Reviews customers leave on the website will show up here for you to approve.</p>
                </div>
            )}

            <div className="mt-8 grid gap-5" data-testid="admin-review-list">
                {visible.map((r) => (
                    <article key={r.id} className="rounded-[1.5rem] bg-white border border-[#F2E1E6] p-6 shadow-[0_12px_30px_rgba(61,35,24,0.06)]" data-testid={`admin-review-${r.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h3 className="font-display text-xl font-bold text-[#3D2318]" data-testid="admin-review-name">{r.name}</h3>
                                <p className="text-xs text-[#8C6A5D]">{r.occasion ? `${r.occasion} · ` : ""}{fmt(r.created_at)}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3D2318]/15"}`} />)}</span>
                                <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${BADGE[r.status]}`} data-testid="admin-review-status">{r.status}</span>
                            </div>
                        </div>
                        <p className="mt-4 whitespace-pre-line text-sm text-[#3D2318] leading-relaxed">“{r.quote}”</p>
                        <div className="mt-5 flex flex-wrap gap-2">
                            {r.status !== "approved" && (
                                <button onClick={() => setStatus(r.id, "approved")} data-testid="admin-review-approve" className="inline-flex items-center gap-1.5 rounded-full bg-[#2f6b3a] px-4 py-2 text-xs font-semibold text-white hover:bg-[#25562e] transition-colors">
                                    <Check className="h-3.5 w-3.5" /> Approve &amp; publish
                                </button>
                            )}
                            {r.status !== "hidden" && (
                                <button onClick={() => setStatus(r.id, "hidden")} data-testid="admin-review-hide" className="inline-flex items-center gap-1.5 rounded-full border border-[#3D2318]/15 bg-white px-4 py-2 text-xs font-semibold text-[#3D2318] hover:border-[#C95B7D] hover:text-[#C95B7D] transition-colors">
                                    <EyeOff className="h-3.5 w-3.5" /> Hide
                                </button>
                            )}
                            <button onClick={() => remove(r.id)} data-testid="admin-review-delete" className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-[#b3261e]/80 hover:bg-[#fdecea] transition-colors">
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}
